// src/components/ActivityPanel.tsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { TbX } from 'react-icons/tb';
import { TaskItem } from '../App';
import './ActivityPanel.css';

interface ActivityPanelProps {
  tasks: TaskItem[];
  onClose: () => void;
  onDayClick?: (date: Date) => void;
  isModal?: boolean;
  width?: number;
  onWidthChange?: (width: number) => void;
}

interface DayData {
  date: Date;
  count: number;
  intensity: 0 | 1 | 2 | 3 | 4;
}

const CELL_SIZE = 24;
const CELL_GAP = 4;
const HEADER_ROW_HEIGHT = 24;

export const ActivityPanel: React.FC<ActivityPanelProps> = ({
  tasks,
  onClose,
  onDayClick,
  isModal = false,
  width = 305,
  onWidthChange,
}) => {
  const [hoveredDay, setHoveredDay] = useState<DayData | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [visibleWeeks, setVisibleWeeks] = useState(20);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate visible weeks based on container height
  const calculateVisibleWeeks = useCallback(() => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.clientHeight;
      const rowHeight = CELL_SIZE + CELL_GAP;
      const availableHeight = containerHeight - HEADER_ROW_HEIGHT - CELL_GAP;
      const weeks = Math.floor(availableHeight / rowHeight);
      setVisibleWeeks(Math.max(4, weeks));
    }
  }, []);

  // Measure container on mount and resize
  useEffect(() => {
    calculateVisibleWeeks();
    const resizeObserver = new ResizeObserver(calculateVisibleWeeks);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [calculateVisibleWeeks]);

  // Handle resize
  useEffect(() => {
    if (!isResizing || isModal) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = e.clientX - 64;
      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isModal, onWidthChange]);

  const heatmapData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate start date - cap at 4 months
    const startDate = new Date(today);
    const maxWeeks = Math.min(visibleWeeks, 18); // ~4 months
    startDate.setDate(startDate.getDate() - (maxWeeks * 7));
    startDate.setHours(0, 0, 0, 0);

    const dateCountMap = new Map<string, number>();

    tasks.forEach(task => {
      if (task.completed && task.completedAt) {
        const completedDate = new Date(task.completedAt);
        completedDate.setHours(0, 0, 0, 0);
        const dateKey = completedDate.toISOString().split('T')[0];
        dateCountMap.set(dateKey, (dateCountMap.get(dateKey) || 0) + 1);
      }
    });

    const days: DayData[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= today) {
      const dateKey = currentDate.toISOString().split('T')[0];
      const count = dateCountMap.get(dateKey) || 0;

      let intensity: 0 | 1 | 2 | 3 | 4 = 0;
      if (count > 0) {
        if (count <= 2) intensity = 1;
        else if (count <= 5) intensity = 2;
        else if (count <= 10) intensity = 3;
        else intensity = 4;
      }

      days.push({
        date: new Date(currentDate),
        count,
        intensity,
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }, [tasks, visibleWeeks]);

  const weekGroups = useMemo(() => {
    const groups: DayData[][] = [];
    let currentWeek: DayData[] = [];

    if (heatmapData.length > 0) {
      const firstDay = heatmapData[0].date.getDay();
      for (let i = 0; i < firstDay; i++) {
        currentWeek.push({
          date: new Date(0),
          count: -1,
          intensity: 0,
        });
      }
    }

    heatmapData.forEach((day, index) => {
      currentWeek.push(day);

      if (day.date.getDay() === 6 || index === heatmapData.length - 1) {
        while (currentWeek.length < 7 && index === heatmapData.length - 1) {
          currentWeek.push({
            date: new Date(0),
            count: -1,
            intensity: 0,
          });
        }
        groups.push(currentWeek);
        currentWeek = [];
      }
    });

    return groups;
  }, [heatmapData]);

  const monthLabelByWeek = useMemo(() => {
    const labelMap = new Map<number, string>();

    weekGroups.forEach((week, weekIndex) => {
      // Find if this week contains the 1st of any month
      const firstOfMonth = week.find(day => day.count !== -1 && day.date.getDate() === 1);
      if (firstOfMonth) {
        labelMap.set(weekIndex, moment(firstOfMonth.date).format('MMM'));
      }
    });

    return labelMap;
  }, [weekGroups]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDayClick = (day: DayData) => {
    if (onDayClick) {
      onDayClick(day.date);
      onClose();
    }
  };

  const panelContent = (
    <div
      className={`activity-panel ${isResizing ? 'resizing' : ''}`}
      style={!isModal ? { width: `${width}px` } : undefined}
    >
      {!isModal && (
        <div
          className="panel-resize-handle"
          onMouseDown={() => setIsResizing(true)}
        />
      )}
      <div className="activity-panel-header">
        <span className="activity-panel-title">Activity</span>
        <button className="activity-panel-close" onClick={onClose} aria-label="Close activity">
          <TbX />
        </button>
      </div>
      <div className="activity-panel-content">
        <div className="activity-heatmap" ref={containerRef}>
          <div className="heatmap-header">
            <h3>Completed Tasks</h3>
          </div>
          <div className="heatmap-container">
            <div className="heatmap-grid">
              <div className="week-row header-row">
                <div className="month-label-cell"></div>
                <div className="weekday-label">S</div>
                <div className="weekday-label">M</div>
                <div className="weekday-label">T</div>
                <div className="weekday-label">W</div>
                <div className="weekday-label">T</div>
                <div className="weekday-label">F</div>
                <div className="weekday-label">S</div>
              </div>

              {weekGroups.map((week, weekIndex) => (
                <div key={weekIndex} className="week-row">
                  <div className="month-label-cell">
                    {monthLabelByWeek.get(weekIndex) || ''}
                  </div>
                  {week.map((day, dayIndex) =>
                    day.count === -1 ? (
                      <div key={dayIndex} className="day-cell empty" />
                    ) : (
                      <div
                        key={dayIndex}
                        className={`day-cell intensity-${day.intensity}${onDayClick ? ' clickable' : ''}`}
                        onMouseEnter={e => {
                          setHoveredDay(day);
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMousePosition({
                            x: rect.left + rect.width / 2,
                            y: rect.top,
                          });
                        }}
                        onMouseLeave={() => setHoveredDay(null)}
                        onClick={() => handleDayClick(day)}
                        data-date={day.date.toISOString()}
                        data-count={day.count}
                      />
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {hoveredDay &&
        ReactDOM.createPortal(
          <div
            className="heatmap-tooltip"
            style={{
              left: `${mousePosition.x}px`,
              top: `${mousePosition.y}px`,
            }}
          >
            <strong>
              {hoveredDay.count} task{hoveredDay.count !== 1 ? 's' : ''}
            </strong>
            <br />
            {moment(hoveredDay.date).format('MMM D, YYYY')}
          </div>,
          document.body
        )}
    </div>
  );

  if (isModal) {
    return (
      <div className="activity-modal" onClick={handleOverlayClick}>
        {panelContent}
      </div>
    );
  }

  return panelContent;
};
